/**
 * Self-contained Databases panel — renders the list of databases for either
 * a client or a project, plus the New Database dialog (with Quick-start
 * templates and column editor). Used in ClientDetail and ProjectDetail tabs.
 *
 * Authorization model (v2.9+):
 *   - Admin / Manager: can create / edit / delete any database
 *   - Executive: can create new databases on clients/projects they have access
 *     to, and can edit + delete ONLY databases they created themselves.
 *     Databases created by Admin or Manager are read-only for Executives.
 *
 * Props:
 *   - clientId  (string?)    — set when scoping to a client
 *   - projectId (string?)    — set when scoping to a project
 *     EXACTLY ONE of clientId / projectId must be provided.
 */
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Database, Trash2, Pencil, FileDown, Settings2, Lock } from "lucide-react";
import { toast } from "sonner";
import { parseApiError } from "@/lib/apiErrors";
import DatabaseSchemaEditor, { newColumn } from "@/components/DatabaseSchemaEditor";
import { FieldInput, FieldDisplay } from "@/components/DatabaseField";

const EMPTY_FORM = () => ({ name: "", description: "", hidden_from_executives: false, columns: [newColumn("Title")] });

export default function DatabasesPanel({ clientId, projectId }) {
  const { user } = useAuth();
  const [dbs, setDbs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM());
  const [templates, setTemplates] = useState([]);

  // Everyone with project/client access can create databases now.
  const canCreate = true;
  // Per-DB edit/delete is computed from creator vs current user inside DatabaseView.

  const listUrl = clientId
    ? `/clients/${clientId}/compliance-dbs`
    : `/projects/${projectId}/compliance-dbs`;

  const load = async () => {
    try {
      const r = await api.get(listUrl);
      setDbs(r.data || []);
    } catch { setDbs([]); }
  };

  useEffect(() => {
    if (!clientId && !projectId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, projectId]);

  useEffect(() => {
    api.get("/database-templates").then((r) => setTemplates(r.data || [])).catch(() => setTemplates([]));
  }, []);

  const applyTemplate = (t) => {
    setForm((f) => ({
      name: f.name || t.label,
      description: f.description || t.description || "",
      columns: t.columns.map((c) => ({ ...c, options: [...(c.options || [])] })),
    }));
    toast.success(`Applied "${t.label}" template`);
  };

  const create = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (!form.columns || form.columns.length === 0) { toast.error("Add at least one field"); return; }
    const keys = form.columns.map((c) => c.key);
    if (keys.some((k) => !k)) { toast.error("Every field needs a name"); return; }
    if (new Set(keys).size !== keys.length) { toast.error("Field names must be unique"); return; }
    try {
      const payload = clientId
        ? { ...form, client_id: clientId }
        : { ...form, project_id: projectId };
      await api.post("/compliance-dbs", payload);
      toast.success("Database created");
      setOpen(false);
      setForm(EMPTY_FORM());
      load();
    } catch (e) {
      toast.error(parseApiError(e, "Failed"));
    }
  };

  return (
    <div data-testid="databases-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs text-slate-500 font-mono-data">{dbs.length} {dbs.length === 1 ? "database" : "databases"}</div>
        {canCreate && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(EMPTY_FORM()); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" data-testid="btn-new-db">
                <Plus size={14} className="mr-1.5" /> New Database
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-w-2xl">
              <DialogHeader><DialogTitle style={{ fontFamily: "Outfit" }}>New Database</DialogTitle></DialogHeader>
              <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
                {templates.length > 0 && (
                  <div className="border border-slate-200 rounded-sm p-3 bg-slate-50/50" data-testid="db-templates-block">
                    <div className="gc-label mb-1.5">Quick start</div>
                    <div className="flex flex-wrap gap-1.5">
                      {templates.map((t) => (
                        <button
                          key={t.key}
                          type="button"
                          onClick={() => applyTemplate(t)}
                          className="text-xs px-2.5 py-1.5 border border-slate-200 hover:border-[#0a2540] hover:text-[#0a2540] rounded-sm text-slate-700 bg-white"
                          title={t.description}
                          data-testid={`btn-tpl-${t.key}`}
                        >
                          {t.label} <span className="font-mono-data text-[10px] text-slate-400">({t.columns.length})</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <Label className="text-xs">Name *</Label>
                  <Input
                    className="mt-1 rounded-sm"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g., GST Tracker, Shareholders, Vendors"
                    data-testid="input-db-name"
                  />
                </div>
                <div>
                  <Label className="text-xs">Description</Label>
                  <Textarea
                    className="mt-1 rounded-sm"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    data-testid="input-db-desc"
                  />
                </div>
                {(user?.role === "Admin" || user?.role === "Manager") && (
                  <label className="flex items-start gap-2.5 cursor-pointer text-xs" data-testid="toggle-hide-from-exec">
                    <input
                      type="checkbox"
                      checked={!!form.hidden_from_executives}
                      onChange={(e) => setForm({ ...form, hidden_from_executives: e.target.checked })}
                      className="mt-0.5"
                    />
                    <span>
                      <div className="font-medium">Hide from Executives</div>
                      <div className="text-[10px] text-slate-500">
                        Executives assigned to this client will not see this database or its reminders.
                      </div>
                    </span>
                  </label>
                )}
                <div className="border-t border-slate-200 pt-3">
                  <DatabaseSchemaEditor
                    columns={form.columns}
                    onChange={(cols) => setForm({ ...form, columns: cols })}
                  />
                  <div className="mt-2 text-[10px] text-slate-400">
                    Tip: add a <strong>Date</strong> field to receive automatic T-7 / T-3 / T-0 reminders {clientId ? "(client-scoped DBs only)" : ""}.
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
                <Button className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" onClick={create} data-testid="btn-create-db">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="space-y-6">
        {dbs.length === 0 && (
          <div className="text-xs text-slate-400 text-center py-8 border border-dashed border-slate-200">
            No databases yet
          </div>
        )}
        {dbs.map((d) => {
          // Per-DB authorization: Admin/Manager can edit any; Executive only their own.
          const canEditDb =
            user?.role === "Admin" ||
            user?.role === "Manager" ||
            d.created_by === user?.user_id;
          // Delete rule: Admin can delete any; Manager + Executive only their own.
          const canDeleteDb =
            user?.role === "Admin" || d.created_by === user?.user_id;
          return (
            <DatabaseView
              key={d.id}
              db={d}
              canEdit={canEditDb}
              canDelete={canDeleteDb}
              ownByMe={d.created_by === user?.user_id}
              onChanged={load}
            />
          );
        })}
      </div>
    </div>
  );
}

function DatabaseView({ db, canEdit, canDelete, ownByMe, onChanged }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [rowOpen, setRowOpen] = useState(false);
  const [rowForm, setRowForm] = useState({});
  const [editingRow, setEditingRow] = useState(null);
  const [schemaOpen, setSchemaOpen] = useState(false);
  const [schemaForm, setSchemaForm] = useState({ name: db.name, description: db.description || "", hidden_from_executives: !!db.hidden_from_executives, columns: db.columns || [] });

  const load = async () => {
    const r = await api.get(`/compliance-dbs/${db.id}/items`);
    setItems(r.data || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [db.id]);

  const openCreate = () => { setEditingRow(null); setRowForm({}); setRowOpen(true); };
  const openEdit = (it) => { setEditingRow(it); setRowForm({ ...(it.data || {}) }); setRowOpen(true); };

  const addRow = async () => {
    try {
      if (editingRow) {
        await api.patch(`/compliance-items/${editingRow.id}`, { data: rowForm });
        toast.success("Updated");
      } else {
        await api.post("/compliance-items", { db_id: db.id, data: rowForm });
        toast.success("Added");
      }
      setRowOpen(false); setRowForm({}); setEditingRow(null);
      load();
    } catch { toast.error("Failed"); }
  };

  const removeRow = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    await api.delete(`/compliance-items/${id}`);
    load();
  };

  const removeDb = async () => {
    if (!window.confirm("Delete this database and all its items?")) return;
    await api.delete(`/compliance-dbs/${db.id}`);
    onChanged();
  };

  const saveSchema = async () => {
    if (!schemaForm.name.trim()) { toast.error("Name is required"); return; }
    const keys = (schemaForm.columns || []).map((c) => c.key);
    if (keys.some((k) => !k)) { toast.error("Every field needs a name"); return; }
    if (new Set(keys).size !== keys.length) { toast.error("Field names must be unique"); return; }
    try {
      await api.patch(`/compliance-dbs/${db.id}`, schemaForm);
      toast.success("Schema saved");
      setSchemaOpen(false);
      onChanged();
    } catch (e) { toast.error(parseApiError(e, "Failed")); }
  };

  return (
    <div className="border border-slate-200">
      <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
        <div className="flex items-center gap-2 min-w-0">
          <Database size={14} className="text-slate-500 shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate flex items-center gap-1.5" style={{ fontFamily: "Outfit" }}>
              {db.name}
              {!canEdit && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-500 text-[9px] font-normal" data-testid={`view-only-${db.id}`}>
                  <Lock size={9} /> View only
                </span>
              )}
              {db.hidden_from_executives && (user?.role === "Admin" || user?.role === "Manager") && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-purple-50 text-purple-700 text-[9px] font-normal border border-purple-200" data-testid={`hidden-exec-${db.id}`}>
                  Hidden from Executives
                </span>
              )}
              {ownByMe && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 text-[9px] font-normal" data-testid={`own-${db.id}`}>
                  Yours
                </span>
              )}
            </div>
            {db.description && <div className="text-xs text-slate-500 truncate">{db.description}</div>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {canEdit && (
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/compliance-dbs/${db.id}/export`}
              className="inline-flex items-center gap-1 h-7 px-2 border border-slate-200 hover:border-[#0a2540] text-xs rounded-sm"
              data-testid={`btn-export-db-${db.id}`}
              title="Export to Excel"
            >
              <FileDown size={12} /> Excel
            </a>
          )}
          {canEdit && (
            <Button
              size="sm"
              variant="outline"
              className="rounded-sm h-7"
              onClick={() => {
                setSchemaForm({ name: db.name, description: db.description || "", hidden_from_executives: !!db.hidden_from_executives, columns: db.columns || [] });
                setSchemaOpen(true);
              }}
              data-testid={`btn-edit-schema-${db.id}`}
            >
              <Settings2 size={12} className="mr-1" /> Schema
            </Button>
          )}
          {canEdit && (
            <Button size="sm" variant="outline" className="rounded-sm h-7" onClick={openCreate} data-testid={`btn-add-row-${db.id}`}>
              <Plus size={12} className="mr-1" /> Entry
            </Button>
          )}
          {canDelete && (
            <Button size="sm" variant="ghost" className="h-7 text-red-600" onClick={removeDb} data-testid={`btn-delete-db-${db.id}`}>
              <Trash2 size={12} />
            </Button>
          )}
        </div>
      </div>

      {/* Capped-height scroll viewport: ~5 dense rows tall before the body
          scrolls vertically. <th> cells are sticky so the column headers
          stay visible while the user scrolls long lists. */}
      <div className="border border-slate-200 overflow-auto max-h-[320px]" data-testid={`db-table-${db.id}`}>
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              {(db.columns || []).map((c) => (
                <th
                  key={c.key}
                  className="text-left px-4 py-2 whitespace-nowrap sticky top-0 z-10 bg-slate-50 border-b border-slate-200"
                >
                  {c.label}
                </th>
              ))}
              {canEdit && (
                <th className="px-4 py-2 w-20 sticky top-0 z-10 bg-slate-50 border-b border-slate-200"></th>
              )}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={(db.columns || []).length + (canEdit ? 1 : 0)} className="px-4 py-6 text-center text-xs text-slate-400">No entries</td></tr>
            )}
            {items.map((it) => (
              <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`row-${it.id}`}>
                {(db.columns || []).map((c) => (
                  <td key={c.key} className="px-4 py-2 text-xs align-top">
                    <FieldDisplay column={c} value={it.data?.[c.key]} />
                  </td>
                ))}
                {canEdit && (
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(it)} className="h-7 w-7 p-0" data-testid={`btn-edit-row-${it.id}`}>
                      <Pencil size={12} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => removeRow(it.id)} className="h-7 w-7 p-0 text-red-600" data-testid={`btn-delete-row-${it.id}`}>
                      <Trash2 size={12} />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={rowOpen} onOpenChange={setRowOpen}>
        <DialogContent className="rounded-sm max-w-xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}>
              {editingRow ? "Edit Entry" : "New Entry"} — {db.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
            {(db.columns || []).map((c) => (
              <div key={c.key}>
                <Label className="text-xs">{c.label}</Label>
                <FieldInput
                  column={c}
                  value={rowForm[c.key]}
                  onChange={(v) => setRowForm({ ...rowForm, [c.key]: v })}
                  testIdPrefix={`row-input-${db.id}`}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setRowOpen(false)}>Cancel</Button>
            <Button className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" onClick={addRow} data-testid="btn-save-row">
              {editingRow ? "Save" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={schemaOpen} onOpenChange={setSchemaOpen}>
        <DialogContent className="rounded-sm max-w-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}>Edit Schema — {db.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
            <div>
              <Label className="text-xs">Database Name *</Label>
              <Input className="mt-1 rounded-sm" value={schemaForm.name} onChange={(e) => setSchemaForm({ ...schemaForm, name: e.target.value })} data-testid="input-schema-name" />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea className="mt-1 rounded-sm" value={schemaForm.description} onChange={(e) => setSchemaForm({ ...schemaForm, description: e.target.value })} data-testid="input-schema-desc" />
            </div>
            {(user?.role === "Admin" || user?.role === "Manager") && (
              <label className="flex items-start gap-2.5 cursor-pointer text-xs" data-testid="toggle-hide-schema">
                <input
                  type="checkbox"
                  checked={!!schemaForm.hidden_from_executives}
                  onChange={(e) => setSchemaForm({ ...schemaForm, hidden_from_executives: e.target.checked })}
                  className="mt-0.5"
                />
                <span>
                  <div className="font-medium">Hide from Executives</div>
                  <div className="text-[10px] text-slate-500">
                    When enabled, this database is invisible to Executives even if the client is assigned to them. Existing reminders from this DB will also be excluded from their dashboard.
                  </div>
                </span>
              </label>
            )}
            <div className="border-t border-slate-200 pt-3">
              <DatabaseSchemaEditor
                columns={schemaForm.columns}
                onChange={(cols) => setSchemaForm({ ...schemaForm, columns: cols })}
              />
              <div className="mt-2 text-[10px] text-slate-400">
                Caution: renaming a field's <strong>key</strong> may orphan existing data. Edit labels freely; change keys only if needed.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setSchemaOpen(false)}>Cancel</Button>
            <Button className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" onClick={saveSchema} data-testid="btn-save-schema">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
